import { getAuth } from "firebase/auth";
import { authProd } from "../../firebase";
import { apiUrlLocal, apiUrlProdFirebase } from "../../globalVariables";
import { isLocalDashboard } from "../OrgUsageAnalytics/api";

// Mounted at /admin/project-views on the backend, behind a verified Firebase
// ID token from the prod project. Jira is the same in every environment, so
// there is no env toggle: on localhost the page talks to the local backend
// (the routes may not be deployed yet), everywhere else to prod.
const BASE = "/project-views";
const TIMEOUT_MS = 60000;

async function call(path, { method = "GET", body } = {}) {
  const user = authProd?.currentUser || getAuth()?.currentUser;
  if (!user) {
    throw new Error("Not authenticated. Please refresh and sign in again.");
  }
  // The admin Cloud Function is named "admin" and mounts its router at
  // "/admin" too, hence the double prefix (same as every other admin page).
  const baseUrl = (isLocalDashboard() ? apiUrlLocal : apiUrlProdFirebase).replace(/\/$/, "");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${baseUrl}/admin${BASE}${path}`, {
      method,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${await user.getIdToken()}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.reason || `Request failed (${res.status})`);
    }

    return data;
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("Jira took too long to answer. Try again.");
    }
    if (error instanceof TypeError) {
      throw new Error(
        isLocalDashboard()
          ? `No backend answered at ${baseUrl}. Start one with npm run dev:webserver.`
          : "Could not reach the server. Try again.",
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export async function listViews() {
  const data = await call("");

  return data.views || [];
}

/** The view and its epics, read live from Jira. */
export async function getView(viewId) {
  return call(`/${encodeURIComponent(viewId)}`);
}

export async function createView(view) {
  return call("", { method: "POST", body: view });
}

export async function updateView(viewId, view) {
  return call(`/${encodeURIComponent(viewId)}`, { method: "PUT", body: view });
}

export async function deleteView(viewId) {
  return call(`/${encodeURIComponent(viewId)}`, { method: "DELETE" });
}

/** Creates a Jira epic carrying the view's labels. */
export async function createEpic(viewId, epic) {
  return call(`/${encodeURIComponent(viewId)}/epics`, { method: "POST", body: epic });
}

/** Writes the epic's Project Step in Jira; an empty step clears it. */
export async function setEpicStep(key, step) {
  return call(`/epics/${encodeURIComponent(key)}/step`, { method: "PUT", body: { step } });
}

/** Renames an epic and/or replaces its plain description in Jira. */
export async function updateEpic(key, changes) {
  return call(`/epics/${encodeURIComponent(key)}`, { method: "PUT", body: changes });
}

/** The Jira status changes allowed for the epic right now. */
export async function listTransitions(key) {
  return call(`/epics/${encodeURIComponent(key)}/transitions`);
}

export async function transitionEpic(key, transitionId) {
  return call(`/epics/${encodeURIComponent(key)}/transition`, {
    method: "POST",
    body: { transitionId },
  });
}

/** The epic's tickets. */
export async function listChildren(key) {
  return call(`/epics/${encodeURIComponent(key)}/children`);
}

export function errorMessage(error) {
  return error?.message || "Something went wrong. Please try again.";
}
